import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Logo from "@/components/Logo";
import {
    ArrowLeft, Phone, Mail, Search, Loader2, MapPin, User, Package, AlertTriangle,
    MessageSquare, StickyNote, Image as ImageIcon, ExternalLink, FileText, FilePlus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
    ny: "Ny",
    under_behandling: "Under behandling",
    afventer_kunde: "Afventer kunde",
    sendt_til_leverandoer: "Sendt til leverandør",
    sendt_til_medarbejder: "Sendt til medarbejder",
    produktion_godkendt: "Produktion godkendt",
    selvbook_sendt: "Selvbook sendt",
    reklamation_booket: "Reklamation booket",
    afvist: "Afvist",
    godkendt: "Godkendt",
    lukket: "Lukket",
};

const DATE_FMT = new Intl.DateTimeFormat("da-DK", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
});

export default function MontorLookup() {
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null); // {found, cases: [...]}
    const [selectedCase, setSelectedCase] = useState(null); // full case data
    const [loadingCase, setLoadingCase] = useState(false);

    const buildQuery = () => {
        const p = phone.trim();
        const e = email.trim();
        return { phone: p || undefined, email: e || undefined };
    };

    const lookup = async (ev) => {
        ev?.preventDefault();
        const p = phone.trim();
        const e = email.trim();
        if (!p && !e) {
            toast.error("Indtast telefonnummer eller email");
            return;
        }
        setLoading(true);
        setResults(null);
        setSelectedCase(null);
        try {
            const { data } = await axios.post(`${API}/montor/lookup`, buildQuery());
            setResults(data);
            if (data.count === 1) {
                // Auto-open the single match
                await openCase(data.cases[0].id);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Kunne ikke slå op");
        } finally {
            setLoading(false);
        }
    };

    const openCase = async (caseId) => {
        setLoadingCase(true);
        try {
            const { data } = await axios.post(`${API}/montor/case/${caseId}`, buildQuery());
            setSelectedCase(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Kunne ikke hente sag");
        } finally {
            setLoadingCase(false);
        }
    };

    const back = () => {
        setSelectedCase(null);
    };

    const reset = () => {
        setPhone("");
        setEmail("");
        setResults(null);
        setSelectedCase(null);
    };

    const fileUrl = (fid, size) => {
        const params = new URLSearchParams();
        if (phone.trim()) params.set("phone", phone.trim());
        if (email.trim()) params.set("email", email.trim());
        if (size) params.set("size", size);
        return `${API}/montor/file/${selectedCase.id}/${fid}?${params.toString()}`;
    };

    // === CASE DETAIL VIEW ===
    if (selectedCase) {
        const c = selectedCase;
        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
            `${c.customer_address}, ${c.customer_postal} ${c.customer_city}`
        )}`;
        return (
            <div className="min-h-screen bg-[#FAFAF9]" data-testid="montor-case-page">
                <div className="max-w-3xl mx-auto px-4 py-6">
                    <button
                        onClick={back}
                        className="flex items-center gap-1.5 text-sm text-[#78736E] hover:text-[#2C2A29] mb-4"
                        data-testid="montor-back-button"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Tilbage
                    </button>

                    <div className="bg-white border border-[#E5E1DA] rounded-xl p-5 mb-5">
                        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Sagsnummer</div>
                                <div className="font-heading text-2xl font-medium text-[#2C2A29] font-mono">{c.case_number}</div>
                            </div>
                            <span className="px-3 py-1 rounded-full bg-[#F0EBE1] text-[#4A4641] text-xs font-semibold uppercase tracking-wider">
                                {STATUS_LABELS[c.status] || c.status}
                            </span>
                        </div>

                        {/* Customer */}
                        <div className="border-t border-[#F0EBE1] pt-4">
                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-2 flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5" /> Kunde
                            </div>
                            <div className="text-base text-[#2C2A29] font-medium">{c.customer_name}</div>
                            <div className="text-sm text-[#5C5854] mt-1">
                                <a href={`tel:${c.customer_phone}`} className="text-[#4A4641] underline">{c.customer_phone}</a>
                            </div>
                            {c.customer_email && (
                                <div className="text-sm text-[#5C5854]">
                                    <a href={`mailto:${c.customer_email}`} className="text-[#4A4641] underline break-all">{c.customer_email}</a>
                                </div>
                            )}
                        </div>

                        {/* Address with maps link */}
                        <div className="border-t border-[#F0EBE1] pt-4 mt-4">
                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-2 flex items-center gap-1.5">
                                <MapPin className="w-3.5 h-3.5" /> Adresse
                            </div>
                            <div className="text-sm text-[#2C2A29]">{c.customer_address}</div>
                            <div className="text-sm text-[#2C2A29]">{c.customer_postal} {c.customer_city}</div>
                            <a
                                href={mapsUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#4A4641] hover:text-[#2C2A29] underline"
                                data-testid="montor-maps-link"
                            >
                                Åbn rute i Google Maps
                                <ExternalLink className="w-3 h-3" />
                            </a>
                        </div>

                        {/* Order info */}
                        {(c.order_number || c.crm?.our_reference) && (
                            <div className="border-t border-[#F0EBE1] pt-4 mt-4">
                                <div className="grid grid-cols-2 gap-3">
                                    {c.order_number && (
                                        <div>
                                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-0.5">Ordrenr.</div>
                                            <div className="text-sm text-[#2C2A29] font-mono">{c.order_number}</div>
                                        </div>
                                    )}
                                    {c.crm?.our_reference && (
                                        <div>
                                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-0.5">Reference</div>
                                            <div className="text-sm text-[#2C2A29] font-mono">{c.crm.our_reference}</div>
                                        </div>
                                    )}
                                    {c.crm?.consultant_name && (
                                        <div>
                                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-0.5">Konsulent</div>
                                            <div className="text-sm text-[#2C2A29]">{c.crm.consultant_name}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Products */}
                    <div className="bg-white border border-[#E5E1DA] rounded-xl p-5 mb-5">
                        <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-2 flex items-center gap-1.5">
                            <Package className="w-3.5 h-3.5" /> Produkt(er)
                        </div>
                        {(c.products || []).map((p, i) => (
                            <div key={i} className="text-sm text-[#2C2A29] py-1">
                                <span className="font-medium">{p.product_type}</span>
                                {p.color_model && <span className="text-[#5C5854]"> · {p.color_model}</span>}
                                {p.room && <span className="text-[#5C5854]"> · {p.room}</span>}
                                <span className="text-[#5C5854]"> · {p.quantity} stk.</span>
                            </div>
                        ))}
                    </div>

                    {/* Problem */}
                    <div className="bg-white border border-[#E5E1DA] rounded-xl p-5 mb-5">
                        <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-2 flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5" /> Problem
                        </div>
                        {c.problem_category && (
                            <div className="text-sm text-[#5C5854] mb-1">Kategori: <span className="text-[#2C2A29]">{c.problem_category}</span></div>
                        )}
                        <div className="text-sm text-[#2C2A29] whitespace-pre-wrap leading-relaxed">{c.problem_description || "-"}</div>
                        {c.wished_solution && (
                            <div className="mt-3 text-sm">
                                <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-0.5">Kundens ønskede løsning</div>
                                <div className="text-[#2C2A29]">{c.wished_solution}</div>
                            </div>
                        )}
                    </div>

                    {/* Images */}
                    {c.file_ids && c.file_ids.length > 0 && (
                        <div className="bg-white border border-[#E5E1DA] rounded-xl p-5 mb-5">
                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-3 flex items-center gap-1.5">
                                <ImageIcon className="w-3.5 h-3.5" /> Billeder og videoer ({c.file_ids.length})
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {c.file_ids.map((fid) => (
                                    <a
                                        key={fid}
                                        href={fileUrl(fid)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="aspect-square block bg-[#F4F2EF] rounded-lg overflow-hidden border border-[#E5E1DA] hover:opacity-90"
                                        data-testid={`montor-file-${fid}`}
                                    >
                                        <img
                                            src={fileUrl(fid, "thumb")}
                                            alt="Bilag"
                                            loading="lazy"
                                            decoding="async"
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.style.display = "none"; e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full text-[#78736E]"><svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16v16H4z"/><path d="M8 10l4 4 4-4"/></svg></div>'; }}
                                        />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {c.receipt_file_id && (
                        <div className="bg-white border border-[#E5E1DA] rounded-xl p-4 mb-5">
                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-2 flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5" /> Kvittering
                            </div>
                            <a
                                href={fileUrl(c.receipt_file_id)}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-[#4A4641] hover:text-[#2C2A29] underline"
                            >
                                Åbn kvittering
                            </a>
                        </div>
                    )}

                    {/* Notes */}
                    {c.notes && c.notes.length > 0 && (
                        <div className="bg-white border border-[#E5E1DA] rounded-xl p-5 mb-5">
                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-3 flex items-center gap-1.5">
                                <StickyNote className="w-3.5 h-3.5" /> Interne noter
                            </div>
                            <div className="space-y-2">
                                {c.notes.map((n, i) => (
                                    <div key={i} className="bg-[#FAF9F7] rounded p-3 text-sm">
                                        <div className="text-xs text-[#78736E] mb-1">
                                            {n.author_name} · {n.created_at && DATE_FMT.format(new Date(n.created_at))}
                                        </div>
                                        <div className="text-[#2C2A29] whitespace-pre-wrap">{n.text}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Internal correspondence (agent <-> supplier/employee) */}
                    {c.internal_messages && c.internal_messages.length > 0 && (
                        <div className="bg-white border border-[#E5E1DA] rounded-xl p-5 mb-5" data-testid="montor-internal-messages">
                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-3 flex items-center gap-1.5">
                                <MessageSquare className="w-3.5 h-3.5" /> Intern korrespondance (leverandør/medarbejder) ({c.internal_messages.length})
                            </div>
                            <div className="space-y-2">
                                {c.internal_messages.map((m, i) => (
                                    <div key={i} className={`rounded-lg p-3 ${m.sender_type === "agent" ? "bg-[#FAF9F7] border border-[#E5E1DA]" : "bg-[#F0EBE1]"}`}>
                                        <div className="text-xs text-[#78736E] mb-1">
                                            {m.sender_name}{m.sender_role_label ? ` (${m.sender_role_label})` : ""}
                                            {m.recipient_name ? ` → ${m.recipient_name}` : ""}
                                            {" · "}{m.created_at && DATE_FMT.format(new Date(m.created_at))}
                                        </div>
                                        <div className="text-sm text-[#2C2A29] whitespace-pre-wrap leading-relaxed">{m.text}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Messages */}
                    {c.messages && c.messages.length > 0 && (
                        <div className="bg-white border border-[#E5E1DA] rounded-xl p-5 mb-5">
                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-3 flex items-center gap-1.5">
                                <MessageSquare className="w-3.5 h-3.5" /> Korrespondance med kunden ({c.messages.length})
                            </div>
                            <div className="space-y-2">
                                {c.messages.map((m, i) => (
                                    <div key={i} className={`rounded-lg p-3 ${m.sender_type === "customer" ? "bg-[#F0EBE1]" : "bg-[#FAF9F7] border border-[#E5E1DA]"}`}>
                                        <div className="text-xs text-[#78736E] mb-1">
                                            {m.sender_name}{m.sender_role ? ` (${m.sender_role})` : ""} · {m.created_at && DATE_FMT.format(new Date(m.created_at))}
                                        </div>
                                        <div className="text-sm text-[#2C2A29] whitespace-pre-wrap leading-relaxed">{m.text}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // === LOOKUP / RESULTS VIEW ===
    return (
        <div className="min-h-screen bg-[#FAFAF9]" data-testid="montor-lookup-page">
            <meta name="robots" content="noindex, nofollow" />
            <div className="max-w-md mx-auto px-4 py-10">
                <div className="flex justify-center mb-6">
                    <Logo size="md" to={null} />
                </div>

                <div className="bg-white border border-[#E5E1DA] rounded-xl p-6">
                    <h1 className="font-heading text-xl font-medium text-[#2C2A29] mb-1">Montør-opslag</h1>
                    <p className="text-sm text-[#78736E] mb-4">Indtast kundens telefonnummer <span className="text-[#78736E]">eller</span> email for at få adgang til sagens oplysninger. Én af felterne er nok.</p>

                    <form onSubmit={lookup} className="space-y-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Kundens telefonnr.</Label>
                            <div className="relative mt-1.5">
                                <Phone className="w-4 h-4 text-[#78736E] absolute left-3 top-1/2 -translate-y-1/2" />
                                <Input
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="fx 12 34 56 78"
                                    className="pl-9"
                                    disabled={loading}
                                    autoFocus
                                    data-testid="montor-phone-input"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-[#A19E9A] uppercase tracking-wider">
                            <div className="flex-1 h-px bg-[#E5E1DA]" />
                            eller
                            <div className="flex-1 h-px bg-[#E5E1DA]" />
                        </div>

                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-[#78736E]">Kundens email</Label>
                            <div className="relative mt-1.5">
                                <Mail className="w-4 h-4 text-[#78736E] absolute left-3 top-1/2 -translate-y-1/2" />
                                <Input
                                    type="email"
                                    inputMode="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="fx kunde@eksempel.dk"
                                    className="pl-9"
                                    disabled={loading}
                                    data-testid="montor-email-input"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading || (!phone.trim() && !email.trim())}
                            className="w-full bg-[#4A4641] hover:bg-[#2C2A29] text-white"
                            data-testid="montor-search-button"
                        >
                            {loading ? (
                                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Søger...</>
                            ) : (
                                <><Search className="w-4 h-4 mr-2" /> Slå op</>
                            )}
                        </Button>
                    </form>

                    {results && !results.found && (
                        <div className="mt-5 rounded-lg bg-[#FAF9F7] border border-[#E5E1DA] p-4 text-sm text-[#5C5854]" data-testid="montor-no-match">
                            Ingen aktive sager fundet på {phone.trim() && email.trim() ? "det telefonnummer eller den email" : phone.trim() ? "det telefonnummer" : "den email"}. Tjek at oplysningen er korrekt indtastet, eller prøv den anden metode — ellers kontakt kontoret.
                        </div>
                    )}

                    {results && results.found && results.count > 1 && (
                        <div className="mt-5" data-testid="montor-results-list">
                            <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-2">
                                {results.count} sager fundet — vælg den rigtige
                            </div>
                            <div className="space-y-2">
                                {results.cases.map((c) => (
                                    <button
                                        key={c.id}
                                        onClick={() => openCase(c.id)}
                                        disabled={loadingCase}
                                        className="w-full text-left bg-[#FAF9F7] hover:bg-[#F0EBE1] border border-[#E5E1DA] rounded-lg p-3 transition-colors disabled:opacity-50"
                                        data-testid={`montor-result-${c.case_number}`}
                                    >
                                        <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                                            <span className="font-mono text-sm font-semibold text-[#2C2A29]">{c.case_number}</span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-[#E5E1DA] text-[#78736E] uppercase">{STATUS_LABELS[c.status] || c.status}</span>
                                        </div>
                                        <div className="text-xs text-[#5C5854]">
                                            {c.customer_name} · {c.customer_city} · {c.created_at && new Date(c.created_at).toLocaleDateString("da-DK")}
                                        </div>
                                        <div className="text-xs text-[#78736E] mt-0.5 truncate">{c.product_summary}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {loadingCase && (
                        <div className="mt-4 text-center text-sm text-[#78736E]">
                            <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Henter sag...
                        </div>
                    )}
                </div>

                <Link
                    to="/montor/ny"
                    className="mt-4 flex items-center justify-center gap-2 w-full bg-[#C2410C] hover:bg-[#9A3412] text-white py-4 px-4 rounded-xl text-sm font-semibold shadow-sm transition-colors"
                    data-testid="montor-report-new-link"
                >
                    <FilePlus className="w-5 h-5" />
                    Indmeld ny reklamation
                </Link>

                <div className="mt-6 text-center text-xs text-[#78736E]">
                    Dette opslag logges. Kontakt kontoret hvis du ikke finder sagen.
                </div>
            </div>
        </div>
    );
}
